import { CogMenu, TimeAgo } from '@/ui'
import type { AdDesignData } from '@jobilla/nexus-sdk'
import ctl from '@netlify/classnames-template-literals'
import { ImageIcon, PenIcon, StarIcon, Trash2Icon } from 'lucide-react'

export interface GalleryCardProps {
	design: AdDesignData
	selected?: boolean
	onSelect?: (design: AdDesignData) => void
	onRename?: (design: AdDesignData) => void
	onDelete?: (design: AdDesignData) => void
	onSetDefaultFeed?: (design: AdDesignData) => void
	onSetDefaultStory?: (design: AdDesignData) => void
	isDefaultFeed?: boolean
	isDefaultStory?: boolean
	showActions?: boolean
}

export function GalleryCard({
	design,
	selected,
	onSelect,
	onRename,
	onDelete,
	onSetDefaultFeed,
	onSetDefaultStory,
	isDefaultFeed,
	isDefaultStory,
	showActions = true,
}: GalleryCardProps) {
	return (
		<div
			className={ctl(`
				relative group rounded-lg border transition-all
				${onSelect ? 'cursor-pointer' : ''}
				${selected ? 'border-brand-500 ring-2 ring-brand-200' : 'border-grey-200 hover:border-grey-300'}
			`)}
			onClick={onSelect ? () => onSelect(design) : undefined}
			onKeyDown={onSelect
				? (e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault()
						onSelect(design)
					}
				}
				: undefined}
			tabIndex={onSelect ? 0 : undefined}
			role={onSelect ? 'button' : undefined}
		>
			{/* Thumbnail */}
			<div className="aspect-[4/5] rounded-t-lg bg-black overflow-hidden flex items-center justify-center">
				{design.thumbnailUrl
					? <img src={design.thumbnailUrl} alt={design.name} className="w-full h-full object-contain" />
					: (
						<div className="w-full h-full flex items-center justify-center">
							<ImageIcon className="size-8 text-grey-300" />
						</div>
					)}
			</div>

			{/* Footer */}
			<div className="px-4 py-3 flex items-center gap-1.5 bg-white rounded-b-lg">
				<div className="flex-1 min-w-0">
					<p className="text-base font-medium text-grey-800">
						{design.name}
					</p>
					<div className="flex gap-1 mt-0.5">
						{isDefaultFeed === true && (
							<span className="text-xs font-medium tracking-wide uppercase bg-purple-100 text-purple-700 px-1.5 py-1 mr-1 rounded">
								Default Feed
							</span>
						)}
						{isDefaultStory === true && (
							<span className="text-xs font-medium tracking-wide uppercase bg-purple-100 text-purple-700 px-1.5 py-1 mr-1 rounded">
								Default Story
							</span>
						)}
						<span className="text-xs py-1 tracking-wide text-grey-500">
							Updated <TimeAgo date={new Date(design.updatedAt)} short={true} />
						</span>
					</div>
				</div>
				{showActions === true && (
					<CogMenu
						className="opacity-0 group-hover:opacity-100 transition-opacity"
						actions={[
							{
								id: 'rename',
								icon: <PenIcon className="size-3" />,
								label: 'Rename',
								onClick: () => onRename?.(design),
							},
							{
								id: 'set-default-feed',
								icon: <StarIcon className="size-3" />,
								label: isDefaultFeed ? 'Unset as default feed' : 'Set as default feed',
								onClick: () => onSetDefaultFeed?.(design),
							},
							{
								id: 'set-default-story',
								icon: <StarIcon className="size-3" />,
								label: isDefaultStory ? 'Unset as default story' : 'Set as default story',
								onClick: () => onSetDefaultStory?.(design),
							},
							{
								id: 'delete',
								icon: <Trash2Icon className="size-3" />,
								label: 'Delete',
								onClick: () => onDelete?.(design),
							},
						]}
					/>
				)}
			</div>
		</div>
	)
}
